import React, { useState, type JSX } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, Mic, Lock, AlertCircle } from 'lucide-react';

interface WalkthroughSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface WelcomeWalkthroughStyledProps {
  onComplete: () => void;
}

/**
 * Redesigned walkthrough for first-time users of the Reflection Guide
 * Follows the same styling pattern as InformedConsent
 */
export function WelcomeWalkthroughStyled({ onComplete }: WelcomeWalkthroughStyledProps): JSX.Element {
  const [currentStep, setCurrentStep] = useState(0);

  const walkthroughSections: WalkthroughSection[] = [
    {
      id: 'introduction',
      title: 'Welcome to Reflection Guide',
      content: (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-[#C2A4D6] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2">Your AI Reflection Partner</h4>
              <p className="text-gray-600">
                The Reflection Guide is an AI-powered tool designed to help you explore your thoughts and feelings, 
                providing a safe space for personal reflection and growth.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <Lock className="w-6 h-6 text-[#B3D9E6] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2">Private and Secure</h4>
              <p className="text-gray-600">
                You can start new conversations anytime, exploring different topics or themes. 
                This is your private space for personal reflection.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F4B6A1]/10 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Key Features</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li>Type messages or record audio</li>
              <li>Private and secure conversations</li>
              <li>Supportive AI guidance for reflection</li>
              <li>Review past conversations anytime</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'textChat',
      title: 'Text Chat',
      content: (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-[#C2A4D6] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2">Type Your Thoughts</h4>
              <p className="text-gray-600">
                You can type messages directly to the Reflection Guide. The guide will respond 
                thoughtfully, asking questions to help you explore your thoughts more deeply.
              </p>
            </div>
          </div>
          
          <div className="bg-[#B3D9E6]/10 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Works Best For</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li>Detailed, thoughtful reflections</li>
              <li>When you prefer writing over speaking</li>
              <li>Quiet environments where speaking isn't ideal</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-[#C2A4D6] pl-4 py-2">
            <h4 className="font-semibold mb-2">Try starting with prompts like:</h4>
            <ul className="space-y-2 text-gray-600">
              <li>"I've been feeling anxious about..."</li>
              <li>"I'm trying to understand why I..."</li>
              <li>"I'm not sure how to handle..."</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'voiceRecording',
      title: 'Voice Recording',
      content: (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Mic className="w-6 h-6 text-[#F4B6A1] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2">Speak Your Thoughts</h4>
              <p className="text-gray-600">
                You can record audio messages by clicking the microphone icon. Your audio will be 
                transcribed automatically, and the guide will respond both with text and an audio reply.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F4B6A1]/10 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Perfect For</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-600">
              <li>Expressing emotions that are hard to put into words</li>
              <li>When you prefer speaking over typing</li>
              <li>Capturing thoughts quickly on the go</li>
            </ul>
          </div>
          
          <div className="bg-gray-100 border-l-4 border-gray-300 p-4 rounded-r-lg">
            <p className="text-gray-600 text-sm">
              <strong>Note:</strong> You'll need to grant microphone permissions in your browser 
              when using this feature for the first time.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'disclaimer',
      title: 'Important Information',
      content: (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-[#F4B6A1] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2">Before You Begin</h4>
              <p className="text-gray-600">
                Please keep these important points in mind when using the Reflection Guide:
              </p>
            </div>
          </div>
          
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
            <h4 className="font-semibold text-red-700 mb-2">Not a Crisis Service</h4>
            <p className="text-gray-700">
              This tool is not designed for emergencies. If you're experiencing a crisis, please contact 
              your therapist, call 988, or reach emergency services at 911.
            </p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-[#F4B6A1] rounded-full mt-2"></div>
              <div>
                <span className="font-semibold">Supplements therapy:</span> 
                <span className="text-gray-600"> The Reflection Guide complements but doesn't replace 
                professional mental health care or therapy.</span>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 bg-[#F4B6A1] rounded-full mt-2"></div>
              <div>
                <span className="font-semibold">Self-reflection tool:</span> 
                <span className="text-gray-600"> This is designed to help you explore your thoughts and feelings, 
                but isn't intended to diagnose or treat medical conditions.</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < walkthroughSections.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentSection = walkthroughSections[currentStep];

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6 md:p-8">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Reflection Guide</h2>
          <span className="text-sm text-gray-600">
            Step {currentStep + 1} of {walkthroughSections.length}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-full bg-[#C2A4D6] rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / walkthroughSections.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">{currentSection.title}</h3>
        {currentSection.content}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            currentStep === 0
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
          Previous
        </button>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-6 py-2 rounded-lg bg-[#C2A4D6] hover:bg-[#B390C9] text-white transition-colors"
        >
          {currentStep === walkthroughSections.length - 1 ? 'Get Started' : 'Next'}
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default WelcomeWalkthroughStyled;